import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object, string } from 'yup';

import { StackedIconCTAProps } from 'Src/components/buttons/StackedIconCTA';
import { CheckOverrideItemPriceRequest } from 'Src/feature/check/models';
import { useCheck } from 'Src/feature/check/useCheck';
import { useCheckItemDiscounts } from 'Src/feature/check-item-discount/useCheckItemDiscounts';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useForm } from 'Src/feature/form/useForm';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useSeatAssignment } from 'Src/feature/seat-assignment/useSeatAssignment';
import { useToast } from 'Src/feature/toasts/useToast';
import { useVoidReason } from 'Src/feature/void-reason/useVoidReason';
import useRoute from 'Src/routes/useRoute';

import { CheckItemEditActionsProps } from './CheckItemEditActions';

const sectionId = 'useCheckItemEditActions';

const useCheckItemEditActions = (options: CheckItemEditActionsProps) => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const toast = useToast();
  const { t } = useTranslation();
  const { crumbs, route } = useRoute();
  const { onGoBack, checkItemButtons } = options;
  const {
    activeCheck,
    selectedCheckItemIds,
    selectedCheckItems,
    activeCheckItem,
    sortedActiveCheckItems,
    sortedActiveCheckItemsWithSeats,
    onMoveItems,
    processing,
  } = checkItemButtons;

  const seatAssignment = useSeatAssignment();

  const removeCheckItem = useCheck.mutate.updateItemStatus();
  const repeatCheckItems = useCheck.mutate.repeatItems();
  const scheduleItems = useCheck.mutate.sendAt();
  const updateItemStatus = useCheck.mutate.updateItemStatus();
  const overrideItemPrice = useCheck.mutate.overrideItemPrice();
  const { data: voidReason } = useVoidReason.query.getAll();

  const voidReasonOptions = voidReason?.items.map((vr) => ({ value: vr.id, label: vr.name }));
  const checkId = activeCheck?.id;
  const selectedUnSentCheckItems = selectedCheckItems.filter(({ status }) => status === 'Added');
  const selectedHeldCheckItems = selectedCheckItems.filter(({ status }) => status === 'IndefiniteHold');
  const selectedScheduledCheckItems = selectedCheckItems.filter(({ sendSchedule }) => !!sendSchedule?.sendAt);
  const selectedUnSplitCheckItems = selectedCheckItems.filter(({ splitBy }) => !splitBy);

  const selectedCheckItemsDiscounts = useCheckItemDiscounts({ checkId, checkItemIds: selectedCheckItemIds });

  // some actions can only be executed when the check item has been added to the db
  const isNewCheckItem = route.is(crumbs.pos.check.item.new);

  const overrideItemPriceForm = useForm<CheckOverrideItemPriceRequest>({
    defaultValues: {
      itemId: activeCheckItem?.id as string,
      price: undefined,
      note: undefined,
    },
  });

  const scheduleItemForm = useForm<{ sendAt: string, isPM: boolean }>({
    defaultValues: { sendAt: '0', isPM: false },
    schema: object().shape({
      sendAt: string().label(t('checkItem.labels.sendAt')).matches(/^(0[1-9]|1[0-2]):([0-5][0-9])$/i, t('checkItem.errors.time')).required(),
    }),
  });

  const voidItemForm = useForm<{ reasonId: number }>({
    defaultValues: { reasonId: undefined },
    schema: object().shape({
      reasonId: number().label(t('voidReason.label')).required(),
    }),
  });

  const modal = {
    voidReason: {
      ...useModalControl({
        onOpen: () => voidItemForm.action.reset({ reasonId: undefined }),
      }),
      onSave: async () => {
        const { isValid, values } = await voidItemForm.action.submit();

        if (!isValid) return log.info('modal.voidReason.onSave()', 'form !isValid');
        if (!checkId) return log.warn('modal.voidReason.onSave', '!checkId');

        const selectedCheckItemIds = selectedCheckItems.map(({ id }) => id);
        const reason = voidReasonOptions?.find((vro) => vro.value === values.reasonId)?.label ?? '';
        removeCheckItem.mutate({
          checkId,
          itemIds: selectedCheckItemIds,
          reasonId: values.reasonId,
          reason,
          status: 'void',
        }, {
          onSuccess: () => {
            modal.voidReason.close();
            onGoBack();
          },
          onError: (e) => toast.show.serviceError(e, { id: `${sectionId}-voidReason-onSave-removeCheckItem` }),
        });
      },
    },
    schedule: {
      ...useModalControl({
        onOpen: () => scheduleItemForm.action.reset({ sendAt: '0', isPM: false }),
      }),
      onSave: async () => {
        const { isValid, values } = await scheduleItemForm.action.submit();

        if (!isValid) return log.info('modal.schedule.onSave()', 'form !isValid');
        if (!checkId) return log.warn('modal.schedule.onSave', '!checkId');
        if (!selectedUnSentCheckItems.length) return log.warn('modal.schedule.onSave', '!selectedUnSentCheckItems.length');

        const selectedUnSentCheckItemIds = selectedUnSentCheckItems.map(({ id }) => id);
        const [sendAtHour, sendAtMinute] = values.sendAt ? values.sendAt.toString().split(':') : ['', ''];
        let sendAtHourInt = parseInt(sendAtHour, 10);
        const sendAtMinuteInt = parseInt(sendAtMinute, 10);
        if (values.isPM) {
          if (sendAtHourInt !== 12) {
            sendAtHourInt = sendAtHourInt + 12;
          }
        } else if (sendAtHourInt === 12) {
          // 12:00 AM -> 00:00
          sendAtHourInt = 0;
        }
        scheduleItems.mutate({
          checkId,
          itemIds: selectedUnSentCheckItemIds,
          sendAt: `${sendAtHourInt.toString().padStart(2, '0')}:${sendAtMinuteInt.toString().padStart(2, '0')}:00`,
        }, {
          onError: (error) => {
            toast.show.serviceError(error, { id: `${sectionId}.scheduleItems` });
            modal.schedule.close();
            onGoBack();
          },
          onSuccess: () => {
            modal.schedule.close();
            onGoBack();
          },
        });
      },
    },
    editPrice: {
      ...useModalControl({
        onOpen: () => overrideItemPriceForm.action.reset({
          itemId: activeCheckItem?.id as string,
          price: undefined,
          note: undefined,
        }),
      }),
      onSave: async () => {
        const { isValid, values } = await overrideItemPriceForm.action.submit();
        if (!isValid) return log.info('modal.editPrice.onSave()', 'form !isValid');
        overrideItemPrice.mutate({
          checkId: checkId as number,
          priceOverrideData: {
            itemId: values.itemId,
            price: parseFloat(values.price?.toString()),
            note: values.note ?? '',
          },
        }, {
          onSuccess: () => {
            modal.editPrice.close();
            onGoBack();
          },
          onError: (e) => toast.show.serviceError(e, { id: `${sectionId}-overrideItemPrice` }),
        });
      },
    },
    splitItem: {
      ...useModalControl(),
      onAfterSave: () => {
        modal.splitItem.close();
        onGoBack();
      },
    },
    discount: {
      ...useModalControl({
        onOpen: () => voidItemForm.action.reset({ reasonId: undefined }),
      }),
      onAfterSave: () => {
        modal.voidReason.close();
        onGoBack();
      },
    },
  };

  // size and padding rules controlled in layout components
  const buttons: Omit<StackedIconCTAProps, 'size' | 'padding'>[] = [];

  // must have a check
  if (checkId) {
    // allow a repeat when multiple selected CheckItem(s)
    if (selectedCheckItems.length) {
      buttons.push({
        ['data-testid']: `${sectionId}-repeat-btn`,
        icon: 'replay',
        label: t('labels.repeat'),
        onClick: () => {
          repeatCheckItems.mutate({
            checkId,
            repeat: {
              items: selectedCheckItems.map((checkItem) => ({
                id: checkItem.id,
                quantity: checkItem.quantity,
                seatNames: checkItem.seatNames,
              })),
            },
          }, {
            onError: (e) => toast.show.serviceError(e, { id: `${sectionId}-repeatCheckItems` }),
          });
        },
      });

      // "discounts" can only be done for one CheckItem at a time
      // "discounts" can only be done if not a new CheckItem
      // "discounts" can only be done if applicable discounts exist for CheckItem
      if (!isNewCheckItem && selectedCheckItemsDiscounts.store.has.any) {
        buttons.push({
          ['data-testid']: `${sectionId}-discount-btn`,
          icon: 'sell',
          label: t('discount.label_plural'),
          onClick: modal.discount.open
        });
      }
    }

    // allow a schedule when multiple selected unsent and unheld CheckItem(s)
    if (selectedUnSentCheckItems.length && !selectedHeldCheckItems.length) {
      buttons.push({
        ['data-testid']: `${sectionId}-schedule-btn`,
        icon: 'schedule',
        label: t('labels.schedule'),
        onClick: modal.schedule.open,
        disabled: activeCheck.status === 'Scheduled',
      });
    }

    // allow a hold when multiple selected unsent and unscheduled CheckItems(s)
    if (selectedUnSentCheckItems.length && !selectedScheduledCheckItems.length) {
      buttons.push({
        ['data-testid']: `${sectionId}-hold-btn`,
        icon: 'front_hand',
        label: t('labels.hold'),
        onClick: () => {
          updateItemStatus.mutate({
            checkId,
            itemIds: selectedUnSentCheckItems.map(({ id }) => id),
            status: 'hold',
          }, {
            onError: (error) => {
              toast.show.serviceError(error, { id: `${sectionId}.holdItems` });
              onGoBack();
            },
            onSuccess: () => {
              onGoBack();
            },
          });
        },
        disabled: activeCheck.status === 'Scheduled',
      });
    }

    // allow a release when multiple selected held CheckItems(s) and no selected unsent CheckItem(s)
    if (selectedHeldCheckItems.length && !selectedUnSentCheckItems.length) {
      buttons.push({
        ['data-testid']: `${sectionId}-release-btn`,
        icon: 'front_hand',
        label: t('labels.release'),
        onClick: () => {
          updateItemStatus.mutate({
            checkId,
            itemIds: selectedHeldCheckItems.map(({ id }) => id),
            status: 'added',
          }, {
            onError: (error) => {
              toast.show.serviceError(error, { id: `${sectionId}.releaseItems` });
              onGoBack();
            },
            onSuccess: () => {
              onGoBack();
            },
          });
        },
      });
    }

    // can only split if all selected items are not already split
    if (!isNewCheckItem && selectedUnSplitCheckItems.length && selectedUnSplitCheckItems.length === selectedCheckItems.length) {
      buttons.push({
        ['data-testid']: `${sectionId}-split-btn`,
        selected: route.params.active.tabId === route.params.value.splitBySeat,
        icon: 'call_split',
        label: t('checkSplit.labels.split'),
        onClick: modal.splitItem.open,
        disabled: activeCheck.status === 'Scheduled',
      });
    }

    // if we have a single CheckItem selection
    // rendered buttons that support single CheckItem actions

    if (selectedCheckItems.length === 1) {
      // "price override" can only be done for one CheckItem at a time
      // "price override" can only be done if not a new CheckItem
      if (!isNewCheckItem) {
        buttons.push({
          ['data-testid']: `${sectionId}-price-btn`,
          icon: 'attach_money',
          label: t('labels.editPrice'),
          onClick: () => modal.editPrice.open(),
        });
      }
    }

    const selectedCheckItemIds = selectedCheckItems.map(({ id }) => id);
    let canDelete = true;
    let canVoid = true;
    selectedCheckItems.forEach((sci) => {
      if (sci.status !== 'Added' && sci.status !== 'IndefiniteHold') canDelete = false;
      if (sci.status !== 'Sent') canVoid = false;
    });
    if (canDelete) {
      buttons.push({
        ['data-testid']: `${sectionId}-delete-btn`,
        icon: 'delete',
        label: t('labels.delete'),
        onClick: () => {
          removeCheckItem.mutate({
            checkId,
            itemIds: selectedCheckItemIds,
            reason: 'Canceled',
            status: 'remove',
          }, {
            onSuccess: () => onGoBack(),
            onError: (e) => {
              toast.show.serviceError(e, { id: `${sectionId}-delete-removeCheckItem` });
              log.error('modal.schedule.onSave', { error: removeCheckItem.error });
            }
          });
        },
      });
    } else if (canVoid) {
      buttons.push({
        ['data-testid']: `${sectionId}-void-btn`,
        icon: 'block',
        label: t('labels.void'),
        onClick: modal.voidReason.open,
      });
    } else {
      buttons.push({
        ['data-testid']: `${sectionId}-delete-btn`,
        icon: 'delete',
        label: t('labels.delete') + '/' + t('labels.void'),
        disabled: true,
      });
    }
  }

  const onMoveItemsUp = () => onMoveItems(selectedCheckItemIds, 'up');

  const onMoveItemsDown = () => onMoveItems(selectedCheckItemIds, 'down');

  const isAllSameSeat = useMemo(() => {
    if (seatAssignment.type === 'UseHotkeys') {
      // when using hotkeys, items can only have a single seat name (thank goodness)
      return selectedCheckItems.every((i) => i.seatNames[0] === selectedCheckItems[0]?.seatNames[0]);
    } else return false;
  }, [seatAssignment.type, selectedCheckItems]);
  const isAllUnsent = useMemo(() => selectedCheckItems.every((i) => i.status === 'Added' && !i.sendSchedule), [selectedCheckItems]);
  const isAllScheduled = useMemo(() => selectedCheckItems.every((i) => i.status === 'Added' && !!i.sendSchedule), [selectedCheckItems]);
  const isAllHeld = useMemo(() => selectedCheckItems.every((i) => i.status === 'IndefiniteHold'), [selectedCheckItems]);

  const shouldDisbleMoveUp = useMemo(() => {
    // disable if there's already a pending network request
    // (repositionItems for obvious reasons, getCheck because that query gets invalidated after repositionItems mutation finishes)
    if (processing.repositionItems || processing.getCheck) return true;
    // if there's a mix of item statuses, we should always disable
    let key: 'Added' | 'Scheduled' | 'IndefiniteHold' | undefined = undefined;
    if (isAllUnsent) key = 'Added';
    else if (isAllScheduled) key = 'Scheduled';
    else if (isAllHeld) key = 'IndefiniteHold';
    // mix of item statuses -> disable
    else return true;
    // when using hotkeys...
    if (seatAssignment.type === 'UseHotkeys') {
      // only enable when selected items belong to the same seat
      if (isAllSameSeat) {
        const targetItemIds = sortedActiveCheckItemsWithSeats.asMapOfRecords.get(selectedCheckItems[0]?.seatNames[0] ?? 'table')?.[key].map(({ id }) => id) ?? [];
        // disable when in first position of group
        if (selectedCheckItemIds.includes(targetItemIds[0])) return true;
        // otherwise enable
        else return false;
      } else {
        // mix of seats -> disable
        return true;
      }
    } else {
      const targetItemIds = (sortedActiveCheckItems.asRecord[key] ?? []).map(({ id }) => id);
      // disable when in first position of group
      if (selectedCheckItemIds.includes(targetItemIds[0])) return true;
      // otherwise enable
      else return false;
    }
  }, [
    isAllHeld,
    isAllSameSeat,
    isAllScheduled,
    isAllUnsent,
    processing.getCheck,
    processing.repositionItems,
    seatAssignment.type,
    selectedCheckItemIds,
    selectedCheckItems,
    sortedActiveCheckItems.asRecord,
    sortedActiveCheckItemsWithSeats.asMapOfRecords,
  ]);

  const shouldDisbleMoveDown = useMemo(() => {
    // disable if there's already a pending network request
    // (repositionItems for obvious reasons, getCheck because that query gets invalidated after repositionItems mutation finishes)
    if (processing.repositionItems || processing.getCheck) return true;
    // if there's a mix of item statuses, we should always disable
    let key: 'Added' | 'Scheduled' | 'IndefiniteHold' | undefined = undefined;
    if (isAllUnsent) key = 'Added';
    else if (isAllScheduled) key = 'Scheduled';
    else if (isAllHeld) key = 'IndefiniteHold';
    // mix of item statuses -> disable
    else return true;
    // when using hotkeys...
    if (seatAssignment.type === 'UseHotkeys') {
      // only enable when selected items belong to the same seat
      if (isAllSameSeat) {
        const targetItemIds = sortedActiveCheckItemsWithSeats.asMapOfRecords.get(selectedCheckItems[0]?.seatNames[0] ?? 'table')?.[key].map(({ id }) => id) ?? [];
        // disable when in last position of group
        if (selectedCheckItemIds.includes(targetItemIds[targetItemIds.length - 1])) return true;
        // otherwise enable
        else return false;
      } else {
        // mix of seats -> disable
        return true;
      }
    } else {
      const targetItemIds = (sortedActiveCheckItems.asRecord[key] ?? []).map(({ id }) => id);
      // disable when in last position of group
      if (selectedCheckItemIds.includes(targetItemIds[targetItemIds.length - 1])) return true;
      // otherwise enable
      else return false;
    }
  }, [
    isAllHeld,
    isAllSameSeat,
    isAllScheduled,
    isAllUnsent,
    processing.getCheck,
    processing.repositionItems,
    seatAssignment.type,
    selectedCheckItemIds,
    selectedCheckItems,
    sortedActiveCheckItems.asRecord,
    sortedActiveCheckItemsWithSeats.asMapOfRecords,
  ]);

  const shouldShowRepositionArrows = useMemo(() => {
    if (seatAssignment.type === 'UseHotkeys') {
      // when using hotkeys, only show arrows if items belong to the same seat
      if (isAllSameSeat) {
        // don't show arrows if new check item
        if (isNewCheckItem) return false;
        else return true;
      } else {
        return false;
      }
    } else {
      // don't show arrows if new check item
      if (isNewCheckItem) return false;
      else return true;
    }
  }, [isAllSameSeat, isNewCheckItem, seatAssignment.type]);

  return {
    isNewCheckItem,
    modal,
    buttons,
    overrideItemPriceForm,
    scheduleItemForm,
    voidItemForm,
    voidReasonOptions,
    activeCheck,
    activeCheckItem,
    onMoveItemsUp,
    onMoveItemsDown,
    disable: {
      moveUp: shouldDisbleMoveUp,
      moveDown: shouldDisbleMoveDown,
    },
    shouldShowRepositionArrows,
  };
};

export { useCheckItemEditActions };
